import { GridChartBaseProps } from "@mantine/charts"
import { Flex, Stack } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { useEffect, useState } from "react"
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Customized,
  Label,
  Line,
  Rectangle,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import { ChangeDataPointOutput } from "@costory/types/endpoints/billy"
import {
  EventsResponses,
  getEventsFromCategory,
} from "@costory/types/endpoints/events"
import { ExploreResponses } from "@costory/types/endpoints/explorer"
import { Filters } from "@costory/types/filters"

import { PREVIOUS_CHART_COLOR } from "@costory/shared/const"
import {
  BUSINESS_METRIC_NAME,
  BUSINESS_METRIC_TECHNICAL_NAME,
  prepareChartData,
  metricLineConfig,
  tickFormatter,
  getFormattedTrendData,
  getTrendDisplayText,
} from "@costory/shared/utils/charts"
import {
  getConsistentChartColor,
  hexToRgba,
} from "@costory/shared/utils/consistentColors"
import {
  computeComparisonPeriodDates,
  isGroupByOptional,
} from "@costory/shared/utils/filters"
import { formatNumber } from "@costory/shared/utils/format"

import { SmartLegend } from "@costory/front/components/SmartLegend"
import {
  ChartTooltip,
  EVENTS_NAME,
} from "@costory/front/components/charts/ChartTooltip"
import { ForecastWarningAlert } from "@costory/front/components/charts/ForecastWarningAlert"
import { useLegendHover } from "@costory/front/components/charts/useLegendHover"
import { RenderEventDot } from "@costory/front/components/common/RenderEventDot"
import { useChartColors } from "@costory/front/components/common/useChartColors"
import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { HandlerEvents } from "@costory/front/pages/Explorer/ExplorerMainChart"
import {
  useExplorerChangeDataPointQuery,
  useExplorerQuery,
} from "@costory/front/queries/explore"
import {
  mergeWithEvents,
  createHandleLegendClick,
  createIsForecastDate,
  splitForecastData,
  getPeriodsToDisplay,
} from "@costory/front/utils/charts"
import { useYAxisLabelConfig } from "@costory/front/utils/format"

type Props = {
  filters: Filters
  height?: number
  withLegend?: boolean
  drillDownInto?: (value: string, mode: "sameWindow" | "newTab") => void
  referenceLines?: GridChartBaseProps["referenceLines"]
  handlerEvents?: HandlerEvents
  events?: EventsResponses.Aggregated
  withMetricsLines?: boolean
  showEventPointer?: boolean
  comparisonData?: ExploreResponses.ExplorerData
  showComparisonWithLegend?: string
  setHideComparisonChart?: (hideComparisonChart: boolean) => void
  isAlertPreview?: boolean
  showTrends?: boolean
  isAnimationActive?: boolean
  filterEventsByChangeDataPointPeriods?: boolean
  isDashboardWidget?: boolean
  showCrossAddEvent?: boolean
  includeForecast?: boolean
  showIntervalBars?: boolean
}

export const StackedBarChart = ({
  filters,
  height,
  drillDownInto,
  referenceLines,
  handlerEvents,
  events,
  withLegend,
  withMetricsLines = false,
  showEventPointer = false,
  comparisonData,
  showComparisonWithLegend,
  setHideComparisonChart,
  isAlertPreview = false,
  showTrends,
  isAnimationActive = true,
  filterEventsByChangeDataPointPeriods = false,
  isDashboardWidget = false,
  showCrossAddEvent = false,
  includeForecast = false,
  showIntervalBars = false,
}: Props) => {
  // Use regular explorer query when forecast is enabled, otherwise use changeDataPoint query
  const explorerQueryWithForecast = useExplorerQuery(filters, includeForecast, {
    includeForecast,
  })
  const explorerQueryChangeDataPoint = useExplorerChangeDataPointQuery(
    filters,
    showTrends,
  )
  const explorerQuery = includeForecast
    ? explorerQueryWithForecast
    : explorerQueryChangeDataPoint
  const [hideMetricLine, { toggle: toggleMetricLine }] = useDisclosure(false)
  const [hideComparisonBar, { toggle: toggleComparisonBar }] =
    useDisclosure(false)
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set())
  const { handleLegendHover, getOpacity } = useLegendHover()
  const { axisTextStyle } = useChartColors()

  const yAxisLabel = useYAxisLabelConfig(filters.metricId, filters.currency)
  const yAxisLabelProps = filters.metricId
    ? { ...yAxisLabel, offset: 0 }
    : undefined
  const [hoverOnEvents, setHoverOnEvents] = useState(false)
  const [tooltipVisible, setTooltipVisible] = useState(true)
  const groupByOptional = isGroupByOptional(filters.groupBy)
  const previousWithColors = comparisonData?.stackedBarChartData.items.map(
    (elem) => ({
      ...elem,
      color: PREVIOUS_CHART_COLOR,
    }),
  )
  useEffect(() => {
    if (setHideComparisonChart) setHideComparisonChart(hideComparisonBar)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideComparisonBar])

  const handleLegendClick = createHandleLegendClick(
    toggleMetricLine,
    toggleComparisonBar,
    setHiddenSeries,
  )

  const filterEventsByChangeDataPointPeriodsActive =
    filterEventsByChangeDataPointPeriods && filters.aggBy == "Day"

  return (
    <QueryWrapper query={explorerQuery}>
      {({ data }) => {
        const changeDataPointPeriods: ChangeDataPointOutput["periods"] =
          "changeDataPointPeriods" in data
            ? (data.changeDataPointPeriods as ChangeDataPointOutput["periods"])
            : []
        const periodsToDisplay = getPeriodsToDisplay(changeDataPointPeriods)
        const chartData = prepareChartData(data, comparisonData)
        const mergedEventsData = mergeWithEvents(
          chartData,
          events,
          filterEventsByChangeDataPointPeriodsActive
            ? periodsToDisplay
            : undefined,
        )

        const isForecastDate = createIsForecastDate(
          includeForecast,
          filters.to,
          {
            aggBy: filters.aggBy as "Month" | "Week" | "Day" | undefined,
          },
        )
        const withColors = data.stackedBarChartData.items.map(
          (elem, index) => ({
            ...elem,
            color: getConsistentChartColor(index),
          }),
        )
        const chartDataWithForecast = splitForecastData(
          mergedEventsData as Record<string, unknown>[],
          includeForecast,
          isForecastDate,
          withColors.map((item) => item.name),
          withMetricsLines ? BUSINESS_METRIC_TECHNICAL_NAME : undefined,
        )
        const legendPayload = [
          ...withColors.map((item) => ({
            value: item.name,
            color: item.color,
            dataKey: item.name,
          })),
          ...(includeForecast
            ? withColors.map((item) => ({
                value: `${item.name} (forecast)`,
                color: hexToRgba(item.color, 0.5),
                dataKey: `${item.name} (forecast)`,
              }))
            : []),
          ...(previousWithColors?.map((item) => ({
            value: `${item.name} (previous)`,
            color: item.color,
            dataKey: `${item.name} (previous)`,
          })) ?? []),
          ...(withMetricsLines
            ? [
                {
                  value: BUSINESS_METRIC_NAME,
                  color: getConsistentChartColor(
                    data.stackedBarChartData.items.length,
                  ),
                  dataKey: BUSINESS_METRIC_TECHNICAL_NAME,
                },
                ...(includeForecast
                  ? [
                      {
                        value: `${BUSINESS_METRIC_NAME} (forecast)`,
                        color: hexToRgba(
                          getConsistentChartColor(
                            data.stackedBarChartData.items.length,
                          ),
                          0.5,
                        ),
                        dataKey: `${BUSINESS_METRIC_TECHNICAL_NAME} (forecast)`,
                      },
                    ]
                  : []),
              ]
            : []),
        ]
        const formattedChangeData = getFormattedTrendData(
          changeDataPointPeriods,
        )

        // Calculate Y-axis domain maximum including forecast confidence interval upper bounds
        const calculateMaxValue = () => {
          const excludedKeys = new Set([
            "agg_date",
            "isForecast",
            "lower",
            "upper",
            "agg_date_previous",
            EVENTS_NAME,
            BUSINESS_METRIC_TECHNICAL_NAME,
          ])
          let maxStackedValue = 0
          let maxUpperBound = 0

          chartDataWithForecast.forEach((entry) => {
            // Sum all positive series values for this date (stacked bar total)
            let dateTotal = 0
            for (const [key, value] of Object.entries(entry)) {
              if (
                !excludedKeys.has(key) &&
                typeof value === "number" &&
                value > 0
              ) {
                dateTotal += value
              }
            }

            maxStackedValue = Math.max(maxStackedValue, dateTotal)

            // Track maximum upper bound from forecast confidence intervals
            if (typeof entry.upper === "number") {
              maxUpperBound = Math.max(maxUpperBound, entry.upper)
            }
          })

          return Math.max(maxStackedValue, maxUpperBound) * 1.1
        }

        const maxYValue = includeForecast ? calculateMaxValue() : undefined

        return (
          <Stack h="100%" gap="xs" style={{ minHeight: 0 }}>
            {includeForecast && data.forecastWarning && (
              <ForecastWarningAlert message={data.forecastWarning} />
            )}
            {withLegend && (!groupByOptional || !!showComparisonWithLegend) && (
              <SmartLegend
                payload={legendPayload}
                onEntryClick={handleLegendClick}
                showComparisonWithLegend={showComparisonWithLegend}
                hideMetricLine={hideMetricLine}
                hideComparison={hideComparisonBar}
                hiddenSeries={hiddenSeries}
                onHoverChange={handleLegendHover}
              />
            )}
            <Flex style={{ flex: 1, minHeight: 0 }}>
              <ResponsiveContainer
                width="100%"
                height={height ?? "100%"}
                style={{ paddingRight: 0 }}
              >
                <ComposedChart
                  data={chartDataWithForecast}
                  stackOffset="sign"
                  style={axisTextStyle}
                  onMouseMove={() => {
                    setTooltipVisible(true)
                  }}
                >
                  <CartesianGrid vertical={false} strokeDasharray="5 5" />
                  <XAxis
                    dataKey="agg_date"
                    angle={30}
                    height={80}
                    textAnchor="start"
                    fontSize={12}
                    tickFormatter={(val: string) =>
                      tickFormatter(val, filters.aggBy)
                    }
                    tick={{
                      cursor: showEventPointer ? "copy" : undefined,
                    }}
                    onClick={(event) => {
                      if (handlerEvents) {
                        handlerEvents.createEventFromXAxisClick(
                          event as unknown as { value: string },
                        )
                      }
                    }}
                    style={axisTextStyle}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(value) =>
                      formatNumber({ value, currency: filters.currency })
                    }
                    fontSize={12}
                    style={axisTextStyle}
                    label={yAxisLabelProps}
                    domain={[
                      (dataMin: number) =>
                        dataMin < 0 && Math.abs(dataMin) < 1 ? 0 : dataMin,
                      maxYValue !== undefined
                        ? (dataMax: number) => Math.max(dataMax, maxYValue)
                        : "auto",
                    ]}
                  />
                  <Tooltip
                    cursor={tooltipVisible}
                    content={({ label, payload, coordinate, active }) => {
                      if (!tooltipVisible || !active) {
                        return null
                      }

                      return (
                        <ChartTooltip
                          label={label}
                          payload={
                            payload as {
                              name: string
                              value: number
                              dataKey: string
                              color: string
                            }[]
                          }
                          currency={filters.currency}
                          coordinate={{
                            x: coordinate?.x ?? 0,
                          }}
                          aggBy={filters.aggBy}
                          displayEvents={hoverOnEvents}
                          events={events && getEventsFromCategory(events)}
                          groupByOptional={groupByOptional}
                          previousDate={
                            payload?.[0]?.payload?.agg_date_previous
                          }
                          hideComparison={hideComparisonBar}
                          isAlertPreview={isAlertPreview}
                          isDashboardWidget={isDashboardWidget}
                          showIntervalBars={showIntervalBars}
                          isBarChart={true}
                        />
                      )
                    }}
                  />
                  {referenceLines?.map(({ label, ...line }, index) => (
                    <ReferenceLine
                      key={index}
                      {...line}
                      fill="var(--mantine-color-info-0)"
                    >
                      <Label
                        value={label}
                        position="top"
                        fontSize={12}
                        fill="var(--mantine-color-textSecondary-0)"
                      />
                    </ReferenceLine>
                  ))}
                  {showTrends &&
                    formattedChangeData?.map(
                      ({ startDate, endDate, color, type }) => {
                        const displayText = getTrendDisplayText(
                          type,
                          startDate,
                          endDate,
                        )
                        return (
                          <ReferenceArea
                            key={startDate}
                            x1={startDate}
                            x2={endDate}
                            fill={color}
                            label={
                              <Label position="inside" fill="white">
                                {displayText}
                              </Label>
                            }
                          />
                        )
                      },
                    )}

                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    yAxisId={BUSINESS_METRIC_TECHNICAL_NAME}
                    orientation="right"
                    fontSize={12}
                    tickFormatter={(value) =>
                      formatNumber({ value, numberType: "decimal" })
                    }
                    style={axisTextStyle}
                  />
                  {previousWithColors?.map((item) => (
                    <Bar
                      key={`previous-${item.name}`}
                      dataKey={`${item.name} (previous)`}
                      stackId="b"
                      fill={item.color}
                      fillOpacity={0.7 * getOpacity(`${item.name} (previous)`)}
                      hide={hideComparisonBar}
                      onMouseEnter={() => setHoverOnEvents(false)}
                      onMouseLeave={() => setHoverOnEvents(false)}
                      name={`${item.name} (previous)`}
                      isAnimationActive={isAnimationActive}
                    />
                  ))}
                  {withColors.map((item, index) => (
                    <Bar
                      key={item.name}
                      dataKey={item.name}
                      fillOpacity={getOpacity(item.name)}
                      onMouseEnter={() => setHoverOnEvents(false)}
                      onMouseLeave={() => setHoverOnEvents(false)}
                      fill={getConsistentChartColor(index)}
                      activeBar={
                        showCrossAddEvent && index === withColors.length - 1 ? (
                          <ActiveBar
                            onClick={(dateValue: string) => {
                              if (handlerEvents) {
                                handlerEvents.createEventFromXAxisClick({
                                  value: dateValue,
                                })
                              }
                            }}
                          />
                        ) : undefined
                      }
                      hide={hiddenSeries.has(item.name)}
                      stackId="a"
                      onClick={(data, _index, event) => {
                        if (!drillDownInto) return
                        const mode = event.metaKey ? "newTab" : "sameWindow"
                        const dataKey = data.tooltipPayload?.[0]?.dataKey as
                          | string
                          | undefined
                        if (!dataKey) return
                        // Normalise forecast series back to the underlying groupBy value
                        const normalizedKey = dataKey.replace(
                          / \(forecast\)$/,
                          "",
                        )
                        drillDownInto(normalizedKey, mode)
                      }}
                      isAnimationActive={isAnimationActive}
                    />
                  ))}
                  {includeForecast &&
                    withColors.map((item, index) => {
                      // Use custom shape for topmost bar to store center position for prediction interval bars
                      const isTopmostBar = index === withColors.length - 1
                      return (
                        <Bar
                          key={`${item.name} (forecast)`}
                          dataKey={`${item.name} (forecast)`}
                          fillOpacity={
                            getOpacity(`${item.name} (forecast)`) * 0.5
                          }
                          onMouseEnter={() => setHoverOnEvents(false)}
                          onMouseLeave={() => setHoverOnEvents(false)}
                          fill={getConsistentChartColor(index)}
                          hide={hiddenSeries.has(`${item.name} (forecast)`)}
                          stackId="a"
                          shape={
                            isTopmostBar
                              ? (barProps: BarShapeProps) => (
                                  <ForecastBarWithIntervalBar
                                    {...barProps}
                                    allData={
                                      chartDataWithForecast as ChartDataEntry[]
                                    }
                                  />
                                )
                              : undefined
                          }
                          onClick={(data, _index, event) => {
                            if (!drillDownInto) return
                            const mode = event.metaKey ? "newTab" : "sameWindow"
                            const dataKey = data.tooltipPayload?.[0]
                              ?.dataKey as string | undefined
                            if (!dataKey) return
                            const normalizedKey = dataKey.replace(
                              / \(forecast\)$/,
                              "",
                            )
                            drillDownInto(normalizedKey, mode)
                          }}
                          isAnimationActive={isAnimationActive}
                        />
                      )
                    })}
                  {includeForecast && showIntervalBars && (
                    <Customized component={ForecastIntervalBars} />
                  )}
                  {withMetricsLines && (
                    <Line
                      stroke={getConsistentChartColor(
                        data.stackedBarChartData.items.length,
                      )}
                      fill={getConsistentChartColor(
                        data.stackedBarChartData.items.length,
                      )}
                      {...metricLineConfig}
                      hide={hideMetricLine}
                      onMouseEnter={() => setHoverOnEvents(false)}
                      onMouseLeave={() => setHoverOnEvents(false)}
                      strokeOpacity={getOpacity(BUSINESS_METRIC_TECHNICAL_NAME)}
                      isAnimationActive={isAnimationActive}
                    />
                  )}
                  {includeForecast && withMetricsLines && (
                    <Line
                      dataKey={`${BUSINESS_METRIC_TECHNICAL_NAME} (forecast)`}
                      name={`${BUSINESS_METRIC_NAME} (forecast)`}
                      yAxisId={BUSINESS_METRIC_TECHNICAL_NAME}
                      stroke={hexToRgba(
                        getConsistentChartColor(
                          data.stackedBarChartData.items.length,
                        ),
                        0.5,
                      )}
                      fill={hexToRgba(
                        getConsistentChartColor(
                          data.stackedBarChartData.items.length,
                        ),
                        0.5,
                      )}
                      strokeWidth={2}
                      connectNulls
                      hide={hideMetricLine}
                      onMouseEnter={() => setHoverOnEvents(false)}
                      onMouseLeave={() => setHoverOnEvents(false)}
                      strokeOpacity={getOpacity(
                        `${BUSINESS_METRIC_TECHNICAL_NAME} (forecast)`,
                      )}
                      isAnimationActive={isAnimationActive}
                    />
                  )}
                  {/* Hidden Y-axis for Events */}
                  {events && (
                    <YAxis
                      yAxisId="events"
                      hide
                      domain={["dataMin", "dataMax"]}
                    />
                  )}

                  {events && (
                    <Scatter
                      dataKey={EVENTS_NAME}
                      yAxisId="events"
                      fill="var(--mantine-color-warning-0)"
                      shape={<RenderEventDot />}
                      onMouseEnter={() => setHoverOnEvents(true)}
                      onMouseLeave={() => setHoverOnEvents(false)}
                      onClick={(val) => {
                        setTooltipVisible(false)
                        setHoverOnEvents(false)
                        if (events && handlerEvents) {
                          handlerEvents.eventClickOnChart(val)
                        }
                      }}
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </Flex>
          </Stack>
        )
      }}
    </QueryWrapper>
  )
}

const ComparisonBarChart = (props: Props) => {
  const { filters } = props

  const shouldFetchComparison = !!filters.previousFrom && !!filters.previousTo

  const previous = useExplorerQuery(
    {
      ...filters,
      from: filters.previousFrom || filters.from,
      to: filters.previousTo || filters.to,
      ...(shouldFetchComparison
        ? computeComparisonPeriodDates(
            filters.previousFrom!,
            filters.previousTo!,
            filters.datePreset,
          )
        : {}),
    },
    shouldFetchComparison,
  )

  if (!shouldFetchComparison) {
    return <StackedBarChart {...props} />
  }

  return (
    <QueryWrapper query={previous}>
      {({ data }) => <StackedBarChart {...props} comparisonData={data} />}
    </QueryWrapper>
  )
}

export const GetBarChart = (props: Props) => {
  return props.showComparisonWithLegend ? (
    <ComparisonBarChart {...props} />
  ) : (
    <StackedBarChart {...props} />
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ActiveBar = (props: any) => {
  const { onClick: onAddClick, ...restProps } = props
  return (
    <>
      <Rectangle fill="pink" stroke="blue" {...restProps} />
      <svg
        x={(props.x ?? 0) + (props.width ?? 0) / 2 - 10}
        y={(props.y ?? 0) - 20}
        width={20}
        height={20}
        fill="white"
        viewBox="0 0 24 24"
        onClick={(event) => {
          event.stopPropagation()
          const dateValue = props.payload?.agg_date || ""
          onAddClick?.(dateValue)
        }}
      >
        <circle
          cx="12"
          cy="12"
          r="10"
          fill="white"
          stroke="black"
          strokeWidth="2"
        />
        <path
          d="M12 6v12M6 12h12"
          stroke="black"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </>
  )
}

// Chart data entry type with forecast bounds
type ChartDataEntry = Record<string, unknown> & {
  agg_date: string
  isForecast?: boolean
  lower?: number | null
  upper?: number | null
  __barCenterX?: number
}

// Bar shape props for Recharts Bar component
interface BarShapeProps {
  x?: number
  y?: number
  width?: number
  height?: number
  payload?: ChartDataEntry
  fill?: string
  fillOpacity?: number
  allData?: ChartDataEntry[]
}

// Custom bar shape that stores the bar center position for prediction interval bar rendering
// The center position is used by ForecastIntervalBars to align interval bars precisely
const ForecastBarWithIntervalBar = (props: BarShapeProps) => {
  const { x, y, width, height, payload, allData } = props

  const barRect = (
    <Rectangle
      x={x}
      y={y}
      width={width}
      height={height}
      fill={props.fill}
      fillOpacity={props.fillOpacity}
    />
  )

  // Store bar center position for prediction interval bar rendering if this is a forecast entry with bounds
  if (
    payload?.isForecast &&
    payload.lower != null &&
    payload.upper != null &&
    allData
  ) {
    const xCenter = (x as number) + (width as number) / 2
    const dataEntry = allData.find(
      (e: ChartDataEntry) => e.agg_date === payload.agg_date,
    )
    if (dataEntry) {
      dataEntry.__barCenterX = xCenter
    }
  }

  return barRect
}

// Axis configuration type
interface AxisConfig {
  yAxisId?: string | number
  scale?: ((value: string | number) => number | undefined) & {
    bandwidth?: () => number
  }
  x?: number
  y?: number
}

// Customized component props for Recharts
interface CustomizedProps {
  xAxisMap?: Record<string, AxisConfig>
  yAxisMap?: Record<string, AxisConfig>
  data?: ChartDataEntry[]
  layout?: {
    offsetX?: number
    offsetY?: number
  }
}

// Renders prediction interval bars for forecast confidence intervals using Recharts Customized component
// Uses bar center positions stored by ForecastBarWithIntervalBar for precise alignment
/**
 * Custom Recharts component that renders confidence interval bars for forecast data points.
 * Displays vertical error bars with horizontal caps to visualize the uncertainty range
 * (lower and upper bounds) of forecast predictions.
 */
const ForecastIntervalBars = (props: CustomizedProps) => {
  const { xAxisMap, yAxisMap, data, layout } = props

  // Early return if required data is missing
  if (!data || !xAxisMap || !yAxisMap) return null

  // Extract X-axis configuration (typically the first/only X-axis)
  const xAxisConfig = Object.values(xAxisMap)[0] as AxisConfig | undefined

  // Extract Y-axis configuration (find the primary Y-axis, typically with yAxisId === 0 or undefined)
  const yAxisConfig = Object.values(yAxisMap).find(
    (config: AxisConfig) => !config.yAxisId || config.yAxisId === 0,
  ) as AxisConfig | undefined

  // Ensure both axes have valid scale functions for coordinate transformation
  if (!xAxisConfig || !yAxisConfig || !xAxisConfig.scale || !yAxisConfig.scale)
    return null

  // Get scale functions for converting data values to pixel coordinates
  const xScale = xAxisConfig.scale // Converts dates/categories to X pixel positions
  const yScale = yAxisConfig.scale // Converts numeric values to Y pixel positions

  // Calculate content area X offset (accounts for Y-axis labels and padding)
  const contentAreaX = layout?.offsetX ?? xAxisConfig.x ?? 0

  // Array to collect all interval bar SVG elements
  const intervalBars: JSX.Element[] = []

  // Width of horizontal caps at the ends of each interval bar (in pixels)
  const capWidth = 8

  // Process each data entry to render confidence intervals for forecast points
  data.forEach((entry: ChartDataEntry, index: number) => {
    // Skip entries that aren't forecasts or don't have confidence interval data
    if (!entry.isForecast || entry.lower == null || entry.upper == null) {
      return
    }

    // Extract confidence interval bounds
    const lower = entry.lower as number // Lower bound of forecast confidence interval
    const upper = entry.upper as number // Upper bound of forecast confidence interval
    const dateStr = entry.agg_date as string // Date/category value for X-axis positioning

    // Convert date/category to X pixel position using the scale function
    const xPos = xScale(dateStr)

    // Skip if scale couldn't determine position (invalid date/category)
    if (xPos == null) return

    // Clamp lower bound to 0 to prevent negative values from appearing above the chart
    // (negative values would render outside the visible chart area)
    const clampedLower = Math.max(0, lower)

    // Convert confidence interval bounds to Y pixel positions
    const yLower = yScale(clampedLower) // Y position for lower bound
    const yUpper = yScale(upper) // Y position for upper bound

    // Skip if scale couldn't determine Y positions
    if (yLower == null || yUpper == null) return

    // Determine the X center position of the interval bar
    // This aligns the bar with the center of the corresponding forecast data point
    const storedCenter = entry.__barCenterX // Pre-calculated bar center (if available)
    let xCenter: number

    if (storedCenter != null) {
      // Use pre-calculated center if available (most accurate)
      xCenter = storedCenter
    } else {
      // Fallback: calculate center from scale bandwidth or data spacing
      const bandwidth = xScale.bandwidth?.()

      if (bandwidth && bandwidth > 0) {
        // For band scales (e.g., categorical bar charts), use bandwidth to find center
        xCenter = (xPos as number) + bandwidth / 2
      } else {
        // For point scales (e.g., time series), estimate center from spacing between consecutive entries
        const forecastEntries = data.filter((e: ChartDataEntry) => e.isForecast)
        const currentIndex = forecastEntries.findIndex(
          (e: ChartDataEntry) => e.agg_date === dateStr,
        )

        if (currentIndex >= 0 && currentIndex < forecastEntries.length - 1) {
          // Calculate center as midpoint between current and next forecast entry
          const nextDate = forecastEntries[currentIndex + 1]?.agg_date as string
          if (nextDate) {
            const nextXPos = xScale(nextDate) as number
            if (nextXPos != null && nextXPos !== xPos) {
              // Midpoint between current and next position
              xCenter =
                (xPos as number) + Math.abs(nextXPos - (xPos as number)) / 2
            } else {
              // Fallback to current position if next position is invalid or same
              xCenter = xPos as number
            }
          } else {
            xCenter = xPos as number
          }
        } else {
          // Last entry or invalid index: use current position as center
          xCenter = xPos as number
        }
      }
    }

    // Calculate final X pixel position, accounting for content area offset
    // If storedCenter exists, it's already in absolute coordinates; otherwise add offset
    const xPixel = storedCenter != null ? xCenter : contentAreaX + xCenter

    // Render three SVG line elements to create the confidence interval bar:
    // 1. Vertical line connecting lower and upper bounds
    // 2. Horizontal cap at the lower bound
    // 3. Horizontal cap at the upper bound
    intervalBars.push(
      // Vertical line: main interval bar
      <line
        key={`interval-bar-${index}-vertical`}
        x1={xPixel}
        y1={yLower as number}
        x2={xPixel}
        y2={yUpper as number}
        stroke="var(--mantine-color-textPrimary-0)"
        strokeWidth={1.5}
        pointerEvents="none" // Prevent interaction (bars are visual indicators only)
      />,
      // Lower cap: horizontal line at the lower bound
      <line
        key={`interval-bar-${index}-lower-cap`}
        x1={xPixel - capWidth / 2}
        y1={yLower as number}
        x2={xPixel + capWidth / 2}
        y2={yLower as number}
        stroke="var(--mantine-color-textPrimary-0)"
        strokeWidth={1.5}
        pointerEvents="none"
      />,
      // Upper cap: horizontal line at the upper bound
      <line
        key={`interval-bar-${index}-upper-cap`}
        x1={xPixel - capWidth / 2}
        y1={yUpper as number}
        x2={xPixel + capWidth / 2}
        y2={yUpper as number}
        stroke="var(--mantine-color-textPrimary-0)"
        strokeWidth={1.5}
        pointerEvents="none"
      />,
    )
  })

  // Return null if no interval bars were created (no valid forecast data)
  if (intervalBars.length === 0) return null

  // Return SVG group containing all interval bars
  return <g>{intervalBars}</g>
}
